import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

const WCAG_AA = 4.5;

// ─── Neighborhood callout ─────────────────────────────────────────────────────
// A centered two-column teaser placed under PinnedCallouts on the Home page.
// Left: a light copy card with an uppercase heading ("THE <Property> NEIGHBORHOOD"),
// the home neighborhood blurb, and an outlined "What's Around Us" CTA.
// Right: a white-framed landscape feature photo that overlaps the card's edge.
// The card and photo each take ~half the section so the composition stays
// balanced, and the layout stacks on mobile.

const imageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 3,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A bright outdoor community or building-exterior photo of the property — courtyard, pool deck, rooftop, landscaped grounds, or the building facade in its neighborhood setting.',
    context:
      'Landscape framed feature photo beside the neighborhood teaser on the Home page. Strongly prefer outdoor community amenities or nice exterior shots; avoid interior room and detail shots.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Link Text', type: 'text', defaultValue: "What's Around Us" },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function NeighborhoodCallout() {
  const image = useSlotGroup('neighborhoodcallout.image', imageDef);
  const cta = useSlotGroup('neighborhoodcallout.cta', ctaDef);

  // Heading, blurb, and outlined CTA sit on `--vibe-color-surface`. Keep the
  // theme primary / text tokens when they clear WCAG AA (4.5:1) against that
  // card; otherwise flip to white or near-black from the surface luminance
  // so pale primaries and inverted surfaces stay readable.
  const tokens = useTokens();
  const copyStyle = useMemo(() => {
    const surface = tokens?.color?.surface || '#f5f5f5';
    const primary = tokens?.color?.primary;
    const text = tokens?.color?.text;
    const fallback = getContrastTextColor(surface);
    const primaryOk = !primary || contrastRatio(primary, surface) >= WCAG_AA;
    const textOk = !text || contrastRatio(text, surface) >= WCAG_AA;
    return {
      ['--nc-title' as string]: primaryOk ? 'var(--vibe-color-primary)' : fallback,
      ['--nc-body' as string]: textOk ? 'var(--vibe-color-text)' : fallback,
      ['--nc-cta' as string]: primaryOk ? 'var(--vibe-color-primary)' : fallback,
      ['--nc-cta-on' as string]: primaryOk
        ? 'var(--vibe-color-text-on-primary, #fff)'
        : getContrastTextColor(fallback),
    };
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="nc-section relative overflow-hidden bg-background">
      <div ref={contentRef} className="reveal-fade-up nc-inner">
        {/* Copy card */}
        <div className="nc-card" style={copyStyle}>
          <RichTextContent
            path="neighborhoodcallout.title"
            label="Title"
            defaultValue='<p>THE <strong><span data-type="field-token" data-field="property_name"></span></strong> NEIGHBORHOOD</p>'
            as="h2"
            className="nc-title font-heading"
          />

          <RichTextContent
            path="neighborhoodcallout.body"
            label="Neighborhood Blurb"
            defaultValue="<p>Discover everything that surrounds you — the cafes, parks, shops, and streets that make this address feel like home.</p><p>From your front door, the best of the neighborhood is only a short walk away.</p>"
            as="div"
            className="nc-body font-body"
          />

          <EditableSlotGroup groupPath="neighborhoodcallout.cta" definition={ctaDef}>
            <a
              href={cta.url || '/neighborhood'}
              target={cta._target || '_self'}
              className="nc-cta font-body"
            >
              {cta.label || "What's Around Us"}
            </a>
          </EditableSlotGroup>
        </div>

        {/* Feature image */}
        <div className="nc-media">
          <EditableSlotGroup
            groupPath="neighborhoodcallout.image"
            definition={imageDef}
            className="nc-framewrap"
          >
            <div className="nc-frame">
              {image.image ? (
                <VibesitesImage
                  url={image.image}
                  alt={image.alt || ''}
                  sizes="(min-width: 900px) 52rem, 100vw"
                  className="nc-img"
                />
              ) : (
                <div className="nc-img nc-img--placeholder" />
              )}
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Mobile: slim side padding so the section uses more of the width.
           Desktop restores even padding on all sides (see media query). */
        .nc-section {
          padding: clamp(4rem, 8vw, 7rem) clamp(1rem, 4vw, 1.75rem);
        }

        /* ── Layout: mobile-first stack, centered ── */
        .nc-inner {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 80rem;
          margin-inline: auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: clamp(1.75rem, 4vw, 2.5rem);
        }

        /* ── Copy card ──
           Mobile-first: centered content; desktop resets to left-aligned. */
        .nc-card {
          position: relative;
          z-index: 2;
          box-sizing: border-box;
          width: 100%;
          max-width: 40rem;
          background: var(--vibe-color-surface, #f5f5f5);
          padding: clamp(1.75rem, 4vw, 3rem);
          border-radius: var(--vibe-radius-sm, 2px);
          text-align: center;
        }
        .nc-title {
          margin: 0 0 clamp(1rem, 2vw, 1.5rem);
          color: var(--nc-title, var(--vibe-color-primary));
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          line-height: 1.1;
          font-size: clamp(1.5rem, 4.5vw, 2.4rem);
          overflow-wrap: break-word;
          word-break: break-word;
        }
        .nc-title strong {
          font-weight: 700;
        }
        .nc-body {
          color: var(--nc-body, var(--vibe-color-text));
          font-size: 1rem;
          line-height: 1.625;
        }
        @media (min-width: 768px) {
          .nc-body { font-size: 1.125rem; }
        }
        .nc-body p {
          margin: 0 0 1rem;
        }
        .nc-body p:last-child {
          margin-bottom: 0;
        }

        /* ── Outlined burgundy CTA ── */
        .nc-cta {
          display: inline-flex;
          align-items: center;
          margin-top: clamp(1.5rem, 3vw, 2.25rem);
          padding: 0.9rem 2.25rem;
          border: 1.5px solid var(--nc-cta, var(--vibe-color-primary));
          border-radius: var(--vibe-radius-sm, 2px);
          color: var(--nc-cta, var(--vibe-color-primary));
          background: transparent;
          font-size: 0.8125rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
        }
        .nc-cta:hover {
          background: var(--nc-cta, var(--vibe-color-primary));
          color: var(--nc-cta-on, var(--vibe-color-text-on-primary, #fff));
          transform: translateY(-2px);
        }

        /* ── Feature image ──
           On the stacked (tablet/mobile) layout the image sits ABOVE the copy
           (order: -1) and matches the card's width. Desktop overrides this with
           absolute positioning, where order has no effect. */
        .nc-media {
          position: relative;
          z-index: 1;
          order: -1;
          width: 100%;
          max-width: 40rem;
        }
        /* The editable wrapper must fill the media box so the frame's
           width:100% resolves to the full column width (matching the card). */
        .nc-framewrap {
          display: block;
          width: 100%;
        }
        .nc-frame {
          box-sizing: border-box;
          width: 100%;
          background: #fff;
          padding: 8px;
          border-radius: var(--vibe-radius-sm, 2px);
          box-shadow: var(--vibe-shadow-lg, 0 10px 30px rgba(0, 0, 0, 0.15));
        }
        .nc-img {
          display: block;
          width: 100%;
          aspect-ratio: 3 / 2;
          object-fit: cover;
          border-radius: 1px;
        }
        .nc-img--placeholder {
          background: var(--vibe-color-surface, #eee);
        }

        /* ── Desktop: a wide gray panel with a large photo laid over its right
           side. The panel is a fixed-height block; the image is absolutely
           positioned and biased upward so gray stays visible below it. ── */
        @media (min-width: 900px) {
          .nc-section {
            padding: clamp(4rem, 8vw, 7rem) clamp(2.5rem, 5vw, 4.5rem);
          }
          .nc-inner {
            display: block;
            gap: 0;
            max-width: 80rem;
          }
          .nc-card {
            width: 58%;
            max-width: none;
            min-height: 40rem;
            display: flex;
            flex-direction: column;
            justify-content: center;
            text-align: left;
            padding: clamp(2.5rem, 4vw, 4rem);
            /* Clear the overlapping image edge. The card is 58% wide and the
               image's left edge sits at 50%, so the 8% offset cancels that
               overlap — leaving a text-to-image gap of clamp(1.75rem,5vw,3rem),
               matching PropertyOverview's description-to-image spacing. */
            padding-right: calc(8% + clamp(1.75rem, 5vw, 3rem));
          }
          .nc-media {
            position: absolute;
            top: 50%;
            right: 0;
            transform: translateY(-50%);
            /* Sized so the photo's left edge stays right of the copy — it only
               overlaps the empty gray part of the panel, never the text. */
            width: 50%;
            max-width: 52rem;
            z-index: 3;
          }
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodCallout',
  displayName: 'Neighborhood Callout',
  tokens: {
    'neighborhoodcallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p>THE <strong><span data-type="field-token" data-field="property_name"></span></strong> NEIGHBORHOOD</p>',
    },
    'neighborhoodcallout.body': {
      label: 'Neighborhood Blurb',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Discover everything that surrounds you — the cafes, parks, shops, and streets that make this address feel like home.</p><p>From your front door, the best of the neighborhood is only a short walk away.</p>',
    },
  },
  slotGroups: {
    'neighborhoodcallout.image': {
      label: 'Feature Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 3,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
      generator: {
        prompt:
          'A bright outdoor community or building-exterior photo of the property — courtyard, pool deck, rooftop, landscaped grounds, or the building facade in its neighborhood setting.',
        context:
          'Landscape framed feature photo beside the neighborhood teaser on the Home page. Strongly prefer outdoor community amenities or nice exterior shots; avoid interior room and detail shots.',
      },
    },
    'neighborhoodcallout.cta': {
      label: 'CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
        label: { label: 'Link Text', type: 'text', defaultValue: "What's Around Us" },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(NeighborhoodCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodCallout;
